import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, FormInput, FormNumberInput, FormSelect } from '@/components/UI';
import { trpc, trpcClient } from '@/api/trpc';
import { showToast } from '@/components/Layout/Toast';
import type { EntryScorecardsRow, TradeLogicComparisonOperatorV2 } from '@/types/appRouter';
import AddRuleSelectorFields from './AddRuleSelectorFields';
import { numericValueKinds, ruleRoleOptions, thresholdOperators } from './constants';
import {
    contextFeatureFormValueToString,
    isScorecardBarDuration,
    parseContextFeatureFormValue,
    parseFeatureValue,
    selectorInputSupportsMultiValue,
    selectorKindOptionsForFeature
} from './helpers';
import type { ManualRuleSelectorKind, ScorecardFeatureRow, ScorecardRuleRole } from './types';

interface AddRuleCardProps {
    scorecard: EntryScorecardsRow;
    canEditScorecard: boolean;
    loadRules: () => Promise<void>;
}

const AddRuleCard = ({ scorecard, canEditScorecard, loadRules }: AddRuleCardProps) => {
    const [addRuleFeatureSearch, setAddRuleFeatureSearch] = useState('');
    const [addRuleFeatureKey, setAddRuleFeatureKey] = useState('');
    const [addRuleSelectorValues, setAddRuleSelectorValues] = useState<Record<string, string>>({});
    const [addRuleSelectorKind, setAddRuleSelectorKind] = useState<ManualRuleSelectorKind>('value');
    const [addRuleValueText, setAddRuleValueText] = useState('');
    const [addRuleValueValues, setAddRuleValueValues] = useState<string[]>([]);
    const [addRuleRangeMin, setAddRuleRangeMin] = useState<number | null>(null);
    const [addRuleRangeMax, setAddRuleRangeMax] = useState<number | null>(null);
    const [addRuleThresholdOperator, setAddRuleThresholdOperator] = useState<TradeLogicComparisonOperatorV2>('>');
    const [addRuleThresholdValue, setAddRuleThresholdValue] = useState<number | null>(null);
    const [addRuleStockLift, setAddRuleStockLift] = useState<number | null>(0);
    const [addRuleOptionLift, setAddRuleOptionLift] = useState<number | null>(0);
    const [addRuleSampleSize, setAddRuleSampleSize] = useState<number | null>(1);
    const [addRuleRole, setAddRuleRole] = useState<ScorecardRuleRole>('boost');
    const [addRulePending, setAddRulePending] = useState(false);

    const authoringFeaturesQuery = useQuery({
        ...trpc.scorecards.listScorecardFeatures.queryOptions({
            surfaces: ['rustBacktest']
        }),
        enabled: canEditScorecard,
        staleTime: 300000
    });

    const authoringFeatureByKey = useMemo(() => {
        return new Map((authoringFeaturesQuery.data ?? []).map((feature) => [feature.featureKey, feature]));
    }, [authoringFeaturesQuery.data]);

    const selectedAuthoringFeature = addRuleFeatureKey ? (authoringFeatureByKey.get(addRuleFeatureKey) ?? null) : null;

    const addRuleSelectorKindOptions = useMemo(() => selectorKindOptionsForFeature(selectedAuthoringFeature), [selectedAuthoringFeature]);

    const addRuleFeatureOptions = useMemo(() => {
        const query = addRuleFeatureSearch.trim().toLowerCase();
        const grouped = new Map<string, { value: string; label: string }[]>();

        for (const feature of authoringFeaturesQuery.data ?? []) {
            if (!feature.canMaterialize) continue;

            const searchable = [feature.label, feature.featureKey, feature.family, feature.providerId, feature.description ?? '']
                .join(' ')
                .toLowerCase();

            if (query && !searchable.includes(query)) continue;

            const group = feature.ui?.group ?? feature.optimizer?.scorecardCategory ?? feature.family;
            const options = grouped.get(group) ?? [];

            options.push({
                value: feature.featureKey,
                label: `${feature.label} (${feature.featureKey})`
            });

            grouped.set(group, options);
        }

        return Array.from(grouped.entries())
            .sort(([leftGroup], [rightGroup]) => leftGroup.localeCompare(rightGroup))
            .map(([label, options]) => ({
                label,
                options: options.sort((left, right) => left.label.localeCompare(right.label))
            }));
    }, [addRuleFeatureSearch, authoringFeaturesQuery.data]);

    const resetManualRuleFormForFeature = useCallback((feature: ScorecardFeatureRow | null) => {
        const nextSelectorValues: Record<string, string> = {};

        for (const [selectorKey, selectorSchema] of Object.entries(feature?.selectors ?? {})) {
            const defaultValue = selectorSchema.defaultValue ?? selectorSchema.allowedValues?.[0] ?? '';
            nextSelectorValues[selectorKey] = contextFeatureFormValueToString(defaultValue);
        }

        setAddRuleSelectorValues(nextSelectorValues);
        setAddRuleSelectorKind('value');
        setAddRuleRangeMin(null);
        setAddRuleRangeMax(null);
        setAddRuleThresholdOperator('>');
        setAddRuleThresholdValue(null);

        const defaultFeatureValue = feature?.valueEnum?.[0] ?? (feature?.valueKind === 'boolean' ? true : undefined);
        const defaultFeatureValueText = contextFeatureFormValueToString(defaultFeatureValue);
        setAddRuleValueText(defaultFeatureValueText);
        setAddRuleValueValues(defaultFeatureValueText ? [defaultFeatureValueText] : []);
    }, []);

    const buildManualRuleSelector = useCallback(() => {
        if (!selectedAuthoringFeature) {
            throw new Error('Select a feature before adding a rule');
        }

        if (addRuleSelectorKind === 'range') {
            if (addRuleRangeMin === null || addRuleRangeMax === null) {
                throw new Error('Range rules require numeric min and max values');
            }

            return { type: 'range' as const, min: addRuleRangeMin, max: addRuleRangeMax };
        }

        if (addRuleSelectorKind === 'threshold') {
            if (addRuleThresholdValue === null) {
                throw new Error('Threshold rules require a numeric value');
            }

            return { type: 'threshold' as const, operator: addRuleThresholdOperator, value: addRuleThresholdValue };
        }

        const rawValues = selectorInputSupportsMultiValue(selectedAuthoringFeature)
            ? addRuleValueValues
            : addRuleValueText
              ? [addRuleValueText]
              : [];

        if (!rawValues.length) {
            throw new Error('Value rules require at least one selected value');
        }

        return {
            type: 'value' as const,
            values: rawValues.map((rawValue) => parseFeatureValue(rawValue, selectedAuthoringFeature))
        };
    }, [
        addRuleRangeMax,
        addRuleRangeMin,
        addRuleSelectorKind,
        addRuleThresholdOperator,
        addRuleThresholdValue,
        addRuleValueText,
        addRuleValueValues,
        selectedAuthoringFeature
    ]);

    const onAddManualRule = useCallback(async () => {
        if (!scorecard || !selectedAuthoringFeature) return;

        try {
            setAddRulePending(true);

            const selectorValues = Object.fromEntries(
                Object.entries(selectedAuthoringFeature.selectors)
                    .filter(([selectorKey, selectorSchema]) => {
                        const rawValue = addRuleSelectorValues[selectorKey] ?? '';

                        if (selectorSchema.required && rawValue === '') {
                            throw new Error(`${selectorSchema.label ?? selectorKey} is required`);
                        }

                        return selectorSchema.required || rawValue !== '';
                    })
                    .map(([selectorKey, selectorSchema]) => [
                        selectorKey,
                        parseContextFeatureFormValue(addRuleSelectorValues[selectorKey] ?? '', selectorSchema)
                    ])
            );

            const contextFeatureRef = {
                kind: 'contextFeature' as const,
                featureKey: selectedAuthoringFeature.featureKey,
                ...(isScorecardBarDuration(selectedAuthoringFeature.defaultDuration)
                    ? { duration: selectedAuthoringFeature.defaultDuration }
                    : {}),
                ...(Object.keys(selectorValues).length ? { selectors: selectorValues } : {})
            };

            const selector = buildManualRuleSelector();
            const optionLift = addRuleOptionLift ?? undefined;
            const stockLift = addRuleStockLift;
            const sampleSize = addRuleSampleSize;

            if (stockLift === null) {
                throw new Error('Stock lift must be numeric');
            }

            if (sampleSize === null || !Number.isInteger(sampleSize) || sampleSize <= 0) {
                throw new Error('Sample size must be a positive integer');
            }

            await trpcClient.scorecards.addRule.mutate({
                scorecardId: scorecard.id,
                contextFeatureRef,
                selectorType: selector.type,
                selector,
                stockLift,
                optionLift,
                sampleSize,
                ruleRole: addRuleRole
            });

            showToast('Rule added', 'success');
            await loadRules();
        } catch (error) {
            showToast(error instanceof Error ? error.message : 'Failed to add rule', 'error');
        } finally {
            setAddRulePending(false);
        }
    }, [
        addRuleRole,
        addRuleOptionLift,
        addRuleSampleSize,
        addRuleSelectorValues,
        addRuleStockLift,
        buildManualRuleSelector,
        loadRules,
        scorecard,
        selectedAuthoringFeature
    ]);

    return (
        <Card className="p-4">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-white text-lg font-semibold">Add Rule</h3>
                <Button disabled={addRulePending || !selectedAuthoringFeature} variant="primary" onClick={onAddManualRule}>
                    {addRulePending ? 'Adding...' : 'Add Rule'}
                </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <FormInput
                    label="Search Features"
                    placeholder="RSI, market regime, Elliott..."
                    value={addRuleFeatureSearch}
                    onChange={(event) => setAddRuleFeatureSearch(event.target.value)}
                />
                <div className="lg:col-span-2">
                    <FormSelect
                        disabled={authoringFeaturesQuery.isLoading}
                        label="Feature"
                        options={[
                            {
                                label: 'Feature',
                                options: [
                                    {
                                        value: '',
                                        label: authoringFeaturesQuery.isLoading ? 'Loading features...' : 'Select feature...'
                                    }
                                ]
                            },
                            ...addRuleFeatureOptions
                        ]}
                        value={addRuleFeatureKey}
                        onChange={(event) => {
                            const featureKey = event.target.value;
                            setAddRuleFeatureKey(featureKey);
                            resetManualRuleFormForFeature(authoringFeatureByKey.get(featureKey) ?? null);
                        }}
                    />
                </div>
            </div>

            {selectedAuthoringFeature && (
                <div className="space-y-4">
                    <div className="rounded border border-zinc-800 bg-zinc-950/40 p-3">
                        <div className="text-sm text-zinc-100">{selectedAuthoringFeature.label}</div>
                        <div className="text-xs text-zinc-500 break-all">{selectedAuthoringFeature.featureKey}</div>
                    </div>

                    <AddRuleSelectorFields
                        feature={selectedAuthoringFeature}
                        selectorValues={addRuleSelectorValues}
                        setSelectorValues={setAddRuleSelectorValues}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <FormSelect
                            label="Selector"
                            options={addRuleSelectorKindOptions}
                            value={addRuleSelectorKind}
                            onChange={(event) => setAddRuleSelectorKind(event.target.value as ManualRuleSelectorKind)}
                        />

                        {addRuleSelectorKind === 'value' && selectedAuthoringFeature.valueEnum?.length ? (
                            <div className="md:col-span-3">
                                <div className="block text-sm font-medium text-zinc-200 mb-1">Values</div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-40 overflow-auto rounded border border-zinc-800 bg-zinc-950/40 p-2">
                                    {selectedAuthoringFeature.valueEnum.map((allowedValue) => {
                                        const value = String(allowedValue);
                                        const checked = addRuleValueValues.includes(value);

                                        return (
                                            <label className="flex items-center gap-2 text-sm text-zinc-200" key={value}>
                                                <input
                                                    checked={checked}
                                                    type="checkbox"
                                                    onChange={(event) =>
                                                        setAddRuleValueValues((previous) =>
                                                            event.target.checked
                                                                ? [...new Set([...previous, value])]
                                                                : previous.filter((item) => item !== value)
                                                        )
                                                    }
                                                />
                                                <span>{value}</span>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                        ) : addRuleSelectorKind === 'value' && selectedAuthoringFeature.valueKind === 'boolean' ? (
                            <FormSelect
                                label="Value"
                                options={[
                                    { value: 'true', label: 'true' },
                                    { value: 'false', label: 'false' }
                                ]}
                                value={addRuleValueText || 'true'}
                                onChange={(event) => setAddRuleValueText(event.target.value)}
                            />
                        ) : addRuleSelectorKind === 'value' ? (
                            <FormInput
                                label="Value"
                                type={numericValueKinds.has(selectedAuthoringFeature.valueKind) ? 'number' : 'text'}
                                value={addRuleValueText}
                                onChange={(event) => setAddRuleValueText(event.target.value)}
                            />
                        ) : null}

                        {addRuleSelectorKind === 'range' && (
                            <>
                                <FormNumberInput label="Min" value={addRuleRangeMin} onValueChange={setAddRuleRangeMin} />
                                <FormNumberInput label="Max" value={addRuleRangeMax} onValueChange={setAddRuleRangeMax} />
                            </>
                        )}

                        {addRuleSelectorKind === 'threshold' && (
                            <>
                                <FormSelect
                                    label="Operator"
                                    options={thresholdOperators
                                        .filter((operator) => selectedAuthoringFeature.allowedOperators.includes(operator))
                                        .map((operator) => ({ value: operator, label: operator }))}
                                    value={addRuleThresholdOperator}
                                    onChange={(event) => setAddRuleThresholdOperator(event.target.value as TradeLogicComparisonOperatorV2)}
                                />
                                <FormNumberInput label="Value" value={addRuleThresholdValue} onValueChange={setAddRuleThresholdValue} />
                            </>
                        )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <FormNumberInput label="Stock Lift (%)" step={0.1} value={addRuleStockLift} onValueChange={setAddRuleStockLift} />
                        <FormNumberInput
                            label="Option Lift (%)"
                            step={0.1}
                            value={addRuleOptionLift}
                            onValueChange={setAddRuleOptionLift}
                        />
                        <FormNumberInput
                            integer
                            label="Sample Size"
                            min={1}
                            value={addRuleSampleSize}
                            onValueChange={setAddRuleSampleSize}
                        />
                        <FormSelect
                            label="Role"
                            options={ruleRoleOptions}
                            value={addRuleRole}
                            onChange={(event) => setAddRuleRole(event.target.value as ScorecardRuleRole)}
                        />
                    </div>
                </div>
            )}
        </Card>
    );
};

export default AddRuleCard;
